import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { CurrentRouteData, RouteDataContext } from '@app/routing/models/current-route-data';
import { RouterService } from '@app/routing/router.service';
import { cached, Cached, CachedOptions } from '@app/utils/cached';
import { CachedEventSourceConnectorFactory } from '@app/utils/cached/cached-event-source-connector-factory.service';
import {
  CachedEventSourceConnector,
  CachedEventSourceConnectorOptions,
} from '@app/utils/cached/cached-event-source-connector.service';
import { portalError } from '@app/utils/error/portal-error';
import { isNotBlank, toDashCase } from '@app/utils/string';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CachedHelperService {
  private routeChangeCallbacks: ((current: CurrentRouteData, old: CurrentRouteData) => void)[] = [];

  constructor(
    private authenticationService: AuthenticationService,
    private eventSourceConnectorFactory: CachedEventSourceConnectorFactory,
    private routerService: RouterService
  ) {
    // If we want to assign signals directly in components (not in
    // ngOnInit anymore), it is necessary that we listen to route changes
    // as soon as they are recognized. Otherwise, it can happen that
    // a component subscribes to a routeAwareCached instance before
    // NavigationEnd has been emitted, meaning that the old dataProducer
    // would be called with the wrong route parameters (because they
    // are singleton functions that are created when a route changes).
    this.routerService.routeDataChangeOnRecognized$.subscribe(({ current, old }) => {
      if (old != null) {
        this.routeChangeCallbacks.forEach((callback) => callback(current, old));
      }
    });
  }

  /**
   * Create a new route aware cached and an event source connector for it.
   */
  routeAwareConnectedCached<T>(
    options: ConnectedRouteAwareCachedOptions<T>
  ): [Cached<T>, CachedEventSourceConnector<T>] {
    const cached = this.routeAwareCached(options);
    const connector = this.eventSourceConnectorFactory.create({
      cached,
      calculateId: (routeDataContext) => this.getInstanceId(options, routeDataContext).id,
      context: options.context,
      watchRemembered: options.rememberTtl != null,
    });

    return [cached, connector];
  }

  /**
   * Create a cached instance that watches for route parameter changes and
   * updates the identity of the cached instance whenever the route parameter
   * changes.
   */
  routeAwareCached<T>(options: RouteAwareCachedOptions<T>): Cached<T> {
    const {
      id,
      dataProducer,
      onRouteParametersChanged,
      requireAllRouteParameters = true,
      routeParameters,
      ...cachedOptions
    } = options;

    const { id: instanceId, dataProducer: instanceProducer } = this.getInstanceId(
      options,
      this.routerService.currentRouteData.context
    );

    const instance: Cached<T> = cached({
      dataProducer: instanceProducer,
      id: instanceId,
      ...cachedOptions,
    });

    this.routeChangeCallbacks.push((current, old) => {
      if (this.authenticationService.isAuthenticated) {
        if (old != null) {
          const parameterValues = routeParameters.map((parameter) => current.params[parameter]);
          const oldValues = routeParameters.map((parameter) => old.params[parameter]);

          const allParametersDefined: boolean = parameterValues.some((value) => value == null) === false;
          const allOldParametersDefined: boolean = oldValues.some((value) => value == null) === false;
          const anyParameterDefined: boolean = parameterValues.some((value) => value != null);
          const parametersChanged: boolean =
            parameterValues.slice().sort().join(',') !== oldValues.slice().sort().join(',');

          if (parametersChanged) {
            if (onRouteParametersChanged != null) {
              onRouteParametersChanged(
                routeParameters.map((_, index) => {
                  return {
                    changed: oldValues[index] !== parameterValues[index],
                    value: parameterValues[index],
                  };
                })
              );
            }

            if (
              (requireAllRouteParameters && allParametersDefined) ||
              (requireAllRouteParameters === false && anyParameterDefined)
            ) {
              const { id: newId, dataProducer: newProducer } = this.getInstanceId(options, current.context);

              instance.changeIdentity(newId, newProducer);
            }
          }

          // in case auto-reload is enabled and the data producer is not a pure function, suspending auto-reload is a must.
          // otherwise, the data producer function will most probably throw an error.
          if (allParametersDefined === false && allOldParametersDefined === true) {
            instance.suspendAutomaticReload();
          }
        }
      }
    });

    return instance;
  }

  /**
   * Creates an id for a route aware cached instance based on the given
   * route parameters. It will look for the first one that is defined
   * and use its identifier as the prefix and its value as the id.
   *
   * @param id Identifier for the instance that is appended after the user level
   * @param routeParameters List of route parameters in the form [identifier, value][]
   */
  routeAwareCachedId({ id, routeParameters }: RouteAwareCachedIdParameters): string {
    const firstDefinedParameter = routeParameters.find(([_, value]) => isNotBlank(value));

    if (firstDefinedParameter == null) {
      throw portalError({
        cause: new Error('Cannot find a route parameter that is defined'),
        id: 'CachedHelperService:routeAwareCachedId',
      });
    }

    const [name, value] = firstDefinedParameter;

    return `${name}:${value}:${id}`;
  }

  private getInstanceId<T>(
    options: RouteAwareCachedOptions<T>,
    routeDataContext: RouteDataContext
  ): { dataProducer: () => Observable<T>; id: string } {
    const parameterValues = options.routeParameters.map(
      (parameter) => this.routerService.currentRouteData.params[parameter]
    );

    const dataProducer: () => Observable<T> = () => options.dataProducer(parameterValues);
    let id: string | null;

    if (typeof options.id === 'string') {
      const routeParameters = options.routeParameters
        .map((name) => [toDashCase(name.replace(/Id$/, '')), routeDataContext[name]])
        .reverse() as [string, string][];

      id = this.routeAwareCachedId({
        id: options.id,
        routeParameters: routeParameters,
      });
    } else {
      id = options.id(parameterValues);
    }

    return {
      dataProducer,
      id,
    };
  }
}

interface RouteAwareCachedIdParameters {
  id: string;
  routeParameters: [/* name */ string, /* value */ string][];
}

/**
 * Options for a cached that watches for route parameter changes and updates the
 * identity of the cached instance whenever the route parameter changes.
 */
interface RouteAwareCachedOptions<T> extends Omit<CachedOptions<T>, 'id' | 'dataProducer'> {
  /**
   * Producer function that is passed to the cached instance.
   * @param value Current value of the route parameters.
   */
  dataProducer: (value: string[]) => Observable<T>;

  /**
   * Function that returns an id for the given route parameter value.
   * @param value Current value of the route parameters.
   */
  id: ((value: string[]) => string) | string;

  /**
   * Callback function that is called whenever a route parameter changes.
   * @param values Current value of the route parameters.
   */
  onRouteParametersChanged?: (values: { changed: boolean; value: string }[]) => void;

  /**
   * If true, then the identity will only be changed if
   * all route parameters are defined (defaults to true).
   */
  requireAllRouteParameters?: boolean;

  /**
   * Route parameters to watch.
   */
  routeParameters: string[];
}

/**
 * Combined options of RouteAwareCached and CachedEventSourceConnector.
 * The properties `cached`, `calculateId` and `watchRemembered` cannot be
 * passed because they will be created/set automatically.
 */
type ConnectedRouteAwareCachedOptions<T> = RouteAwareCachedOptions<T> &
  Omit<CachedEventSourceConnectorOptions<T>, 'cached' | 'calculateId' | 'watchRemembered'>;
